import {Component, Input, QueryList, ViewChildren} from '@angular/core';
import {AsaxBookmarkingService} from 'asax-bookmarking';
import {filter, tap} from 'rxjs/operators';
import {merge} from 'rxjs';
import {TableContainerComponent} from './search-table/table-container/table-container.component';
import {AsaxResultTableService, AsaxVoService, Tab, TabId} from 'asax-common';
import {VoTableContainerComponent} from "./vo-table/vo-table-container/vo-table-container.component";

const activeTabIdQueryParam = 'result_view';
const activeTabIdDefault: TabId = 'observations';

const numericalOperatorsTooltip = 'Available operators: "<" (less than), ">" (greater than), ".." (range), "|" (or)';
const regexOperatorsTooltip = 'Available operators: "*" (any text), "?" (any single character), "|" (or)';

export const tabs: Tab[] = [
  {
    id: 'observations',
    type: 'search-table',
    title: 'Observations',
    icon: 'eye',
    table: {
      index: 'observations',
      columns: [
        {
          description: 'ALMA project code',
          id: 'observationsProjectCode',
          name: 'Project code',
          prop: 'project.code',
          frozenLeft: true,
          selected: true,
          type: 'project',
          width: 120,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '64*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Name of the ALMA source name as registered by the PI.Partial matches through wildcards (?, *), and boolean OR expressions (|), can be used',
          frozenLeft: true,
          id: 'observationsSourceName',
          name: 'ALMA source name',
          prop: 'sourceName',
          selected: true,
          width: 140,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          uType: 'Observation.Target.name',
          ucd: 'meta.id;pos',
          xType: 'adql:VARCHAR',
          arraySize: '256*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Right Ascension of the telescope pointing',
          format: 'ra',
          id: 'observationsRa',
          name: 'RA',
          prop: 'ra',
          selected: true,
          units: [
            'deg',
            'h:m:s'
          ],
          width: 80,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          uType: 'Characterisation.SpatialAxis.Coverage.Location.coord.Position2D.Value2.C1',
          ucd: 'pos.eq.ra;meta.main',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Declination of the telescope pointing',
          format: 'dec',
          id: 'observationsDec',
          name: 'Dec',
          prop: 'dec',
          selected: true,
          units: [
            'd:m:s',
            'deg'
          ],
          width: 80,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          uType: 'Characterisation.SpatialAxis.Coverage.Location.coord.Position2D.Value2.C2',
          ucd: 'pos.eq.dec;meta.main',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'ALMA receiver band',
          id: 'observationsBand',
          name: 'Band',
          prop: 'band',
          selected: true,
          width: 50,
          elasticValidatorType: 'number',
          elasticParserType: 'bands',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '30*',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Continuum sensitivity',
          id: 'observationsContinuumSensitivity',
          name: 'Cont.sens.',
          longName: 'Continuum sensitivity',
          prop: 'continuumSensitivity',
          selected: true,
          units: [
            'mJy/beam',
            'Jy/beam',
            '&mu;Jy/beam',
          ],
          width: 110,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Frequency ranges, resolutions, line-sensitivities and polarization.Frequencies are the LSRK reference frame.',
          id: 'observationsFrequency',
          name: 'Frequency support',
          prop: 'freqMin',
          propsToBeExported: ['energy.freqMin', 'energy.freqMax', 'energy.resMax', 'energy.sens10', 'energy.sensBand', 'energy.polProduct', 'energy.chaNum'],
          exportColumnFormatterType: 'frequency-support',
          props: ['freqMax'],
          selected: true,
          type: 'frequency-support',
          width: 135,
          elasticParserType: 'frequency',
          elasticValidatorType: 'number',
          dataType: 'char',
          ucd: 'em.freq;obs;meta.main',
          uType: 'Char.SpectralAxis.Coverage.Location.support',
          xType: 'adql:VARCHAR',
          arraySize: '2700*',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Release date',
          id: 'observationsReleaseDate',
          name: 'Release date',
          prop: 'releaseDate',
          selected: true,
          type: 'release-date',
          width: 90,
          elasticParserType: 'date',
          elasticValidatorType: 'date',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '64*',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Number of refereed publications making use of these data',
          id: 'observationsNumberPublications',
          name: 'Publications',
          prop: 'numberPublications',
          selected: true,
          type: 'publications',
          width: 60,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'int',
          xType: 'adql:INTEGER',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Angular resolution, estimated from the used baselines',
          id: 'observationsSpatialResolution',
          name: 'Ang.res.',
          longName: 'Angular resolution',
          prop: 'spatialResolution',
          selected: true,
          width: 95,
          units: [
            'deg',
            'arcmin',
            'arcsec',
            'miliarsec'
          ],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Estimated best velocity resolution of all the spectral windows of this observation',
          id: 'observationsVelocityResolution',
          name: 'Min.vel.res.',
          longName: 'Minimum velocity resolution',
          prop: 'velocityResolution',
          selected: true,
          units: [
            'km/s',
            'm/s'
          ],
          width: 105,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          ucd: 'phys.veloc;spect.resolution',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Type(s) of ALMA antenna(s) used for that observation.',
          id: 'observationsArray',
          name: 'Array',
          prop: 'array',
          selected: true,
          width: 60,
          elasticParserType: 'regex',
          elasticValidatorType: 'short-regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Whether or not the observation is a combination of overlapping beams',
          id: 'observationsMosaic',
          name: 'Mosaic',
          prop: 'mosaic',
          selected: true,
          width: 70,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '16*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Largest angular scale that can be recovered with the observation, estimated from the used baselines',
          id: 'observationsLargestAngularScale',
          name: 'Max.reco.scale',
          longName: 'Maximum recoverable scale',
          prop: 'largestAngularScale',
          selected: true,
          width: 120,
          units: [
            'deg',
            'arcmin',
            'arcsec',
            'miliarsec'
          ],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          ucd: 'pos.angResolution;stat.max',
          uType: 'Characterisation.SpatialAxis.Coverage.Bounds.limits.Interval.HiLim',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Field of view, estimated from the used baselines',
          id: 'observationsFov',
          name: 'FOV',
          longName: 'Field of view',
          prop: 'fov',
          selected: true,
          width: 100,
          units: [
            'deg',
            'arcmin',
            'arcsec',
            'miliarsec'
          ],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          ucd: 'phys.angSize;instr.fov',
          uType: 'Characterisation.SpatialAxis.Coverage.Bounds',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Scientific category generated from the Scientific Keywords provided by the PI',
          id: 'observationsScientificCategory',
          name: 'Scientific category',
          prop: 'project.scientificCategory',
          selected: true,
          width: 165,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '200*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Keywords of the proposal as provided by the PI',
          id: 'observationsScienceKeyword',
          name: 'Science keyword',
          prop: 'project.scienceKeyword',
          selected: true,
          width: 200,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '200*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Average integration time of the observation',
          id: 'observationsIntegration',
          name: 'Int.Time',
          longName: 'Integration time',
          prop: 'integration',
          selected: true,
          units: [
            'h',
            'm',
            's'
          ],
          width: 90,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          ucd: 'time.duration;obs.exposure',
          uType: 'Char.TimeAxis.Coverage.Support.Extent',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Galactic longitude of the observation',
          id: 'observationsGalacticLongitude',
          name: 'Gal.lon.',
          longName: 'Galactic longitude',
          prop: 'galacticLongitude',
          selected: true,
          width: 110,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          ucd: 'pos.galactic.lon',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Galactic latitude of the observation',
          id: 'observationsGalacticLatitude',
          name: 'Gal.lat.',
          longName: 'Galactic latitude',
          prop: 'galacticLatitude',
          selected: true,
          width: 110,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          ucd: 'pos.galactic.lat',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Ecliptic longitude of the observation',
          id: 'observationsEclipticLongitude',
          name: 'Ecl.lon.',
          longName: 'Ecliptic longitude',
          prop: 'eclipticLongitude',
          selected: true,
          width: 110,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          ucd: 'pos.ecliptic.lon',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Ecliptic latitude of the observation',
          id: 'observationsEclipticLatitude',
          name: 'Ecl.lat.',
          longName: 'Ecliptic latitude',
          prop: 'eclipticLatitude',
          selected: true,
          width: 110,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          ucd: 'pos.ecliptic.lat',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Estimated best frequency resolution of all the spectral windows of the observation',
          id: 'observationsFrequencyResolution',
          name: 'Min.freq.res.',
          longName: 'Mimimum frequency resolution',
          prop: 'frequencyResolution',
          selected: true,
          width: 120,
          units: [
            'GHz',
            'MHz',
            'kHz'
          ],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          ucd: 'spect.resolution',
          uType: 'Char.SpectralAxis.Resolution.refval',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Type of polarisation products provided',
          id: 'observationsPolProducts',
          name: 'Pol products',
          prop: 'polProducts',
          selected: true,
          width: 100,
          elasticParserType: 'regex',
          elasticValidatorType: 'short-regex',
          dataType: 'char',
          ucd: 'phys.polarization;meta.code',
          xType: 'adql:VARCHAR',
          arraySize: '64*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Length that includes the 5th percentile of all projected baselines calculated from the unweighted UV distribution.',
          id: 'observationsBaselineL5',
          name: 'L5 BL',
          longName: 'L5 baseline',
          prop: 'baselineL5',
          selected: true,
          width: 80,
          units: [
            'km',
            'm'
          ],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'float',
          ucd: 'baselinelength.l5',
          xType: 'adql:FLOAT',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Length that includes the 80th percentile of all projected baselines calculated from the unweighted UV distribution.',
          id: 'observationsBaselineL80',
          name: 'L80 BL',
          longName: 'L80 baseline',
          prop: 'baselineL80',
          selected: true,
          width: 80,
          units: [
            'km',
            'm'
          ],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'float',
          ucd: 'baselinelength.l80',
          xType: 'adql:FLOAT',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Observation date',
          id: 'observationsObservationDate',
          name: 'Obs.date',
          longName: 'Observation date',
          prop: 'observationDate',
          selected: true,
          width: 90,
          elasticParserType: 'date',
          elasticValidatorType: 'date',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '*',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'End date',
          id: 'observationsEndDate',
          name: 'End date',
          longName: 'End date',
          prop: 'endDate',
          selected: true,
          width: 90,
          elasticParserType: 'date',
          elasticValidatorType: 'date',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '*',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Full name of the Principal Investigator of the project',
          id: 'observationsPiName',
          name: 'PI name',
          prop: 'project.piName',
          selected: true,
          width: 130,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.id.PI',
          xType: 'adql:VARCHAR',
          arraySize: '256*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Name of the Scheduling Block used for this observation',
          id: 'observationsSbName',
          name: 'SB name',
          prop: 'sbName',
          selected: true,
          width: 140,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.code;obs;instr.setup',
          xType: 'adql:VARCHAR',
          arraySize: '128*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Full name of the Co-Investigators',
          id: 'observationsProposalAuthors',
          name: 'Proposal authors',
          prop: 'project.coiName',
          selected: true,
          width: 150,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.id.CoI',
          xType: 'adql:VARCHAR',
          arraySize: '512*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Estimated sensitivity of a spectroscopic line of 10km/s width',
          id: 'observationsLineSensitivity',
          name: 'Line sens.(10 km/s)',
          longName: 'Line sensitivity (10 km/s)',
          prop: 'lineSensitivity',
          selected: true,
          units: [
            'mJy/beam',
            'Jy/beam',
            '&mu;Jy/beam',
          ],
          width: 150,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'double',
          xType: 'adql:DOUBLE',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Estimated average amount of precipitable water vapour',
          id: 'observationsPwv',
          name: 'PWV',
          longName: 'Precipitable water vapour',
          prop: 'pwv',
          selected: true,
          width: 60,
          units: [
            'mm',
            'cm'
          ],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'float',
          ucd: 'obs.atmos.extinction',
          xType: 'adql:FLOAT',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Group Observation Unit Set ID',
          id: 'observationsGous',
          name: 'Group ous id',
          prop: 'gous',
          selected: true,
          width: 155,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '64*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Member Observation Unit Set ID',
          id: 'observationsMous',
          name: 'Member ous id',
          prop: 'mous',
          selected: true,
          width: 155,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '64*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Number of ALMA Science Data Model UID',
          id: 'observationsNumberAsdms',
          name: 'Asdm',
          prop: 'numberAsdms',
          selected: true,
          type: 'asdms',
          width: 50,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'int',
          xType: 'adql:INTEGER',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Title of the project as submitted by the PI',
          id: 'observationsProjectTitle',
          name: 'Project title',
          prop: 'project.title',
          selected: true,
          width: 160,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.title;obs.proposal',
          xType: 'adql:VARCHAR',
          arraySize: '256*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'ALMA type of the project',
          id: 'observationsProjectType',
          name: 'Type',
          prop: 'project.type',
          selected: true,
          width: 50,
          elasticParserType: 'regex',
          elasticValidatorType: 'short-regex',
          dataType: 'char',
          ucd: 'meta.code.class;obs.proposal',
          xType: 'adql:VARCHAR',
          arraySize: '16*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Joint proposals',
          id: 'observationsJointProposals',
          name: 'Joint proposals',
          prop: 'project.jointProposalPartner',
          selected: true,
          width: 130,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.id.JointProposals',
          xType: 'adql:VARCHAR',
          arraySize: '512*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Intent of the observation',
          id: 'observationsScanIntent',
          name: 'Scan intent',
          prop: 'scanIntent',
          selected: true,
          width: 130,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.code.class;obs',
          xType: 'adql:VARCHAR',
          arraySize: '256*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Status of the ALMA quality assurance.SemiPass data are data that are valid but the request of the PI has not (fully) been met.',
          id: 'observationsQa2Status',
          name: 'QA2 Status',
          prop: 'qa2Status',
          selected: true,
          width: 110,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.flag',
          xType: 'adql:CHAR',
          arraySize: '16*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Related external product collections which are available for download from the Archive',
          id: 'observationsExternalCollections',
          name: 'Collections',
          prop: 'externalCollections',
          selected: true,
          width: 85,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.flag',
          xType: 'adql:CHAR',
          arraySize: '16*',
          operatorsTooltip: regexOperatorsTooltip
        }
      ],
      headerSearchFiltersMappingSearchProperties: {
        // Header search filters mapping search properties
        //    Position
        sourceNameResolver: {property: 'footprint'},
        sourceName: {property: 'sourceName'},
        raDec: {property: {ra: 'ra', dec: 'dec', footprint: 'footprint'}},
        galactic: {
          property: {
            longitude: 'galacticLongitude',
            latitude: 'galacticLatitude',
            ra: 'ra',
            dec: 'dec',
            footprint: 'footprint'
          }
        },
        ecliptic: {
          property: {
            longitude: 'eclipticLongitude',
            latitude: 'eclipticLatitude',
            ra: 'ra',
            dec: 'dec',
            footprint: 'footprint'
          }
        },
        targetList: {property: {sourceName: 'sourceName', ra: 'ra', dec: 'dec', footprint: 'footprint'}},
        spatialResolution: {property: 'spatialResolution'},
        largestAngularScale: {property: 'largestAngularScale'},

        //    Energy
        frequency: {
          path: 'energy',
          property: {frequencyMin: 'energy.freqMin', frequencyMax: 'energy.freqMax'},
          isNested: true
        },
        band: {property: 'band'},
        spectralResolution: {path: 'energy', property: 'energy.resMin', isNested: true},
        continuumSensitivity: {property: 'continuumSensitivity'},
        lineSensitivity: {property: 'lineSensitivity'},

        //    Project
        projectCode: {property: 'project.code'},
        projectTitle: {property: 'project.title'},
        projectAbstract: {property: 'project.proposalAbstract'},
        piName: {property: 'project.piName'},
        proposalAuthors: {property: 'project.coiName'},
        scienceKeywordIds: {property: 'project.scienceKeywordIds'},

        //    Publication
        bibCode: {path: 'publications', property: 'publications.bibCode', isNested: true},
        title: {path: 'publications', property: 'publications.title', isNested: true},
        bibAbstract: {path: 'publications', property: 'publications.bibAbstract', isNested: true},
        firstAuthor: {path: 'publications', property: 'publications.firstAuthor', isNested: true},
        authors: {path: 'publications', property: 'publications.authors', isNested: true},

        //    Observations
        observationDate: {property: 'observationDate'},
        polarisationType: {path: 'energy', property: 'energy.polNum', isNested: true},
        mous: {property: 'mous'},
        publicData: {property: 'releaseDate'},
        objectTypes: {
          objectTypesSimbad: {longtype: 'longtypesSimbad', isBestMatch: 'bestLongtypeSimbad'},
          objectTypesNed: {longtype: 'longtypesNed', isBestMatch: 'bestLongtypeNed'}
        },
        calibratorObservations: {property: 'isScience'}
      },
      columnsSearchFiltersMappingSearchProperties: {
        observationsProjectCode: {property: 'project.code'},
        observationsSourceName: {property: 'sourceName'},
        observationsRa: {property: 'ra'},
        observationsDec: {property: 'dec'},
        observationsBand: {property: 'band'},
        observationsContinuumSensitivity: {property: 'continuumSensitivity'},
        observationsFrequency: {
          path: 'energy',
          property: {frequencyMin: 'energy.freqMin', frequencyMax: 'energy.freqMax'},
          isNested: true
        },
        observationsReleaseDate: {property: 'releaseDate'},
        observationsNumberPublications: {property: 'numberPublications'},
        observationsSpatialResolution: {property: 'spatialResolution'},
        observationsVelocityResolution: {property: 'velocityResolution'},
        observationsArray: {property: 'array'},
        observationsMosaic: {property: 'mosaic'},
        observationsLargestAngularScale: {property: 'largestAngularScale'},
        observationsFov: {property: 'fov'},
        observationsScientificCategory: {property: 'project.scientificCategory'},
        observationsScienceKeyword: {property: 'project.scienceKeyword'},
        observationsIntegration: {property: 'integration'},
        observationsGalacticLongitude: {property: 'galacticLongitude'},
        observationsGalacticLatitude: {property: 'galacticLatitude'},
        observationsEclipticLongitude: {property: 'eclipticLongitude'},
        observationsEclipticLatitude: {property: 'eclipticLatitude'},
        observationsFrequencyResolution: {property: 'frequencyResolution'},
        observationsPolProducts: {property: 'polProducts'},
        observationsBaselineL80: {property: 'baselineL80'},
        observationsBaselineL5: {property: 'baselineL5'},
        observationsObservationDate: {property: 'observationDate'},
        observationsPiName: {property: 'project.piName'},
        observationsSbName: {property: 'sbName'},
        observationsProposalAuthors: {property: 'project.coiName'},
        observationsPwv: {property: 'pwv'},
        observationsGous: {property: 'gous'},
        observationsMous: {property: 'mous'},
        observationsNumberAsdms: {property: 'numberAsdms'},
        observationsProjectTitle: {property: 'project.title'},
        observationsProjectType: {property: 'project.type'},
        observationsJointProposals: {property: 'project.jointProposalPartner'},
        observationsScanIntent: {property: 'scanIntent'},
        observationsQa2Status: {property: 'qa2Status'},
        observationsExternalCollections: {property: 'externalCollections'}
      },
      aladinPolygonSearchFiltersMappingSearchProperties: {astrofilt: {property: 'footprint'}},
      elasticDSLPositionAggregationQuery: {
        decMin: {min: {field: 'decMin'}},
        decMax: {max: {field: 'decMax'}},
        raMin: {min: {field: 'raMin'}},
        raMax: {max: {field: 'raMax'}},
        ra180Min: {min: {field: 'ra180Min'}},
        ra180Max: {max: {field: 'ra180Max'}},
      },
      elasticDSLFrequencyRangeAggregationQuery: {
        frequencyRange: {
          nested: {path: 'energy'},
          aggs: {
            freqMin: {min: {field: 'energy.freqMin'}},
            freqMax: {max: {field: 'energy.freqMax'}}
          }
        },
      },
      elasticDSLRedshiftAggregationQuery: {
        redshiftAvg: {
          percentiles: {
            field: 'redshift',
            percents: [50]
          }
        }
      }
    }
  },
  {
    id: 'projects',
    type: 'search-table',
    title: 'Projects',
    icon: 'lightbulb',
    table: {
      index: 'projects',
      columns: [
        {
          description: 'ALMA project code',
          id: 'projectsCode',
          frozenLeft: true,
          name: 'Project Code',
          prop: 'code',
          selected: true,
          type: 'project',
          width: 130,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '64*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Title of the project as submitted by the PI',
          id: 'projectsTitle',
          name: 'Project Title',
          prop: 'title',
          selected: true,
          width: 600,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.title;obs.proposal',
          xType: 'adql:VARCHAR',
          arraySize: '256*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'ALMA type of the project',
          id: 'projectsType',
          name: 'Type',
          prop: 'type',
          selected: true,
          width: 50,
          elasticParserType: 'regex',
          elasticValidatorType: 'short-regex',
          dataType: 'char',
          ucd: 'meta.code.class;obs.proposal',
          xType: 'adql:VARCHAR',
          arraySize: '16*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Joint proposals',
          id: 'projectsJointProposals',
          name: 'Joint proposals',
          prop: 'jointProposalPartner',
          selected: true,
          width: 130,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.id.JointProposals',
          xType: 'adql:VARCHAR',
          arraySize: '512*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Full name of the Principal Investigator of the project',
          id: 'projectsPiName',
          name: 'PI Name',
          prop: 'piName',
          selected: true,
          width: 130,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.id.PI',
          xType: 'adql:VARCHAR',
          arraySize: '256*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Full name of the Co-Investigators',
          id: 'projectsProposalAuthors',
          name: 'Proposal authors',
          prop: 'coiName',
          selected: true,
          width: 140,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          ucd: 'meta.id.CoI',
          xType: 'adql:VARCHAR',
          arraySize: '512*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Date at which the last of the data-sets of this project becomes/became publicly available',
          id: 'projectsMaxReleaseDate',
          name: 'Max.Release Date',
          longName: 'Maximum Release Date',
          prop: 'releaseDate',
          selected: true,
          elasticParserType: 'date',
          elasticValidatorType: 'date',
          width: 90,
          parser: 'date',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '64*',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Number of refereed publications making use of these data',
          id: 'projectsNumberPublications',
          name: 'Publications',
          longName: 'Number publications',
          prop: 'numberPublications',
          selected: true,
          type: 'publications',
          width: 70,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'int',
          xType: 'adql:INTEGER',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Number of Observations',
          id: 'projectsNumberObservations',
          name: 'Observations',
          prop: 'numberObservations',
          selected: true,
          type: 'observations',
          width: 70,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'int',
          xType: 'adql:INTEGER',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Name of the Scheduling Block used as a template for executing the ASDM containing this Field',
          id: 'projectsSbNames',
          name: 'SB names',
          prop: 'sbNames',
          selected: true,
          width: 400,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '264*',
          operatorsTooltip: regexOperatorsTooltip
        }
      ],
      headerSearchFiltersMappingSearchProperties: {
        // Header search filters mapping search properties
        //    Position
        sourceNameResolver: {path: 'observations', property: 'observations.footprint', isNested: true},
        sourceName: {path: 'observations', property: 'observations.sourceName', isNested: true},
        raDec: {
          path: 'observations',
          property: {ra: 'observations.ra', dec: 'observations.dec', footprint: 'observations.footprint'},
          isNested: true
        },
        galactic: {
          path: 'observations',
          property: {
            longitude: 'observations.galacticLongitude',
            latitude: 'observations.galacticLatitude',
            ra: 'observations.ra',
            dec: 'observations.dec',
            footprint: 'observations.footprint'
          },
          isNested: true
        },
        ecliptic: {
          path: 'observations',
          property: {
            longitude: 'observations.eclipticLongitude',
            latitude: 'observations.eclipticLatitude',
            ra: 'observations.ra',
            dec: 'observations.dec',
            footprint: 'observations.footprint'
          },
          isNested: true
        },
        targetList: {
          path: 'observations',
          property: {
            sourceName: 'observations.sourceName',
            ra: 'observations.ra',
            dec: 'observations.dec',
            footprint: 'observations.footprint'
          },
          isNested: true
        },
        spatialResolution: {path: 'observations', property: 'observations.spatialResolution', isNested: true},
        largestAngularScale: {path: 'observations', property: 'observations.largestAngularScale', isNested: true},

        //    Energy
        frequency: {
          path: 'observations.energy',
          property: {frequencyMin: 'observations.energy.freqMin', frequencyMax: 'observations.energy.freqMax'},
          isNested: true
        },
        band: {path: 'observations', property: 'observations.band', isNested: true},
        spectralResolution: {path: 'observations.energy', property: 'observations.energy.resMin', isNested: true},
        continuumSensitivity: {path: 'observations', property: 'observations.continuumSensitivity', isNested: true},
        lineSensitivity: {path: 'observations', property: 'observations.lineSensitivity', isNested: true},

        //    Project
        projectCode: {property: 'code'},
        projectTitle: {property: 'title'},
        projectAbstract: {property: 'proposalAbstract'},
        piName: {property: 'piName'},
        proposalAuthors: {property: 'coiName'},
        scienceKeywordIds: {property: 'scienceKeywordIds'},

        //    Publication
        bibCode: {path: 'publications', property: 'publications.bibCode', isNested: true},
        title: {path: 'publications', property: 'publications.title', isNested: true},
        bibAbstract: {path: 'publications', property: 'publications.bibAbstract', isNested: true},
        firstAuthor: {path: 'publications', property: 'publications.firstAuthor', isNested: true},
        authors: {path: 'publications', property: 'publications.authors', isNested: true},

        //    Observations
        observationDate: {path: 'observations', property: 'observations.observationDate', isNested: true},
        polarisationType: {path: 'observations.energy', property: 'observations.energy.polNum', isNested: true},
        mous: {path: 'observations', property: 'observations.mous', isNested: true},
        publicData: {path: 'observations', property: 'observations.releaseDate', isNested: true},
        objectTypes: {
          path: 'observations',
          objectTypesSimbad: {longtype: 'observations.longtypesSimbad', isBestMatch: 'observations.bestLongtypeSimbad'},
          objectTypesNed: {longtype: 'observations.longtypesNed', isBestMatch: 'observations.bestLongtypeNed'},
          isNested: true,
        },
        calibratorObservations: {path: 'observations', property: 'observations.isScience', isNested: true}
      },
      columnsSearchFiltersMappingSearchProperties: {
        projectsCode: {property: 'code'},
        projectsTitle: {property: 'title'},
        projectsType: {property: 'type'},
        projectsJointProposals: {property: 'jointProposalPartner'},
        projectsPiName: {property: 'piName'},
        projectsProposalAuthors: {property: 'coiName'},
        projectsMaxReleaseDate: {property: 'releaseDate'},
        projectsNumberPublications: {property: 'numberPublications'},
        projectsNumberObservations: {property: 'numberObservations'},
        projectsSbNames: {property: 'sbNames'}
      },
      aladinPolygonSearchFiltersMappingSearchProperties: {
        astrofilt: {
          path: 'observations',
          property: 'observations.footprint',
          isNested: true
        }
      },
      elasticDSLPositionAggregationQuery: {
        position: {
          nested: {
            path: 'observations'
          },
          aggs: {
            decMin: {min: {field: 'observations.decMin'}},
            decMax: {max: {field: 'observations.decMax'}},
            raMin: {min: {field: 'observations.raMin'}},
            raMax: {max: {field: 'observations.raMax'}},
            ra180Min: {min: {field: 'observations.ra180Min'}},
            ra180Max: {max: {field: 'observations.ra180Max'}}
          }
        }
      },
      elasticDSLFrequencyRangeAggregationQuery: {
        frequencyRange: {
          nested: {path: 'observations.energy'},
          aggs: {
            freqMin: {min: {field: 'observations.energy.freqMin'}},
            freqMax: {max: {field: 'observations.energy.freqMax'}}
          }
        },
      },
      elasticDSLRedshiftAggregationQuery: {
        redshift: {
          nested: {path: 'observations'},
          aggs: {
            redshiftAvg: {
              percentiles: {field: 'observations.redshift', percents: [50]}
            }
          }
        }
      }
    }
  },
  {
    id: 'publications',
    type: 'search-table',
    title: 'Publications',
    icon: 'book',
    table: {
      index: 'publications',
      columns: [
        {
          description: 'ADS Bibliography Code',
          id: 'publicationsBibCode',
          frozenLeft: true,
          name: 'BibCode',
          prop: 'bibCode',
          selected: true,
          type: 'bib-code',
          width: 180,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '30*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'First author of the refereed publication',
          id: 'publicationsFirstAuthor',
          name: 'First Author',
          prop: 'firstAuthor',
          selected: true,
          width: 130,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '255*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Journal',
          id: 'publicationsJournal',
          name: 'Journal',
          prop: 'journal',
          selected: true,
          width: 90,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '100*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Year of the publication',
          id: 'publicationsYear',
          name: 'Year',
          prop: 'publicationYear',
          selected: true,
          width: 70,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'int',
          xType: 'adql:INTEGER',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Title of the publication',
          id: 'publicationsTitle',
          name: 'Publication Title',
          prop: 'title',
          selected: true,
          width: 600,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '255*',
          operatorsTooltip: regexOperatorsTooltip
        },
        {
          description: 'Date at which the last of the data-sets used in that publication becomes/became publicly available',
          id: 'publicationsMaxReleaseDate',
          name: 'Max.Release Date',
          longName: 'Maximum Release Date',
          prop: 'releaseDate',
          selected: true,
          elasticParserType: 'date',
          elasticValidatorType: 'date',
          width: 90,
          parser: 'date',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '64*',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Projects',
          id: 'publicationsNumberProjects',
          name: 'Projects',
          prop: 'numberProjects',
          selected: true,
          type: 'projects',
          width: 70,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'int',
          xType: 'adql:INTEGER',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Number of Observations',
          id: 'publicationsNumberObservations',
          name: 'Observations',
          prop: 'numberObservations',
          selected: true,
          type: 'observations',
          width: 70,
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          dataType: 'int',
          xType: 'adql:INTEGER',
          operatorsTooltip: numericalOperatorsTooltip
        },
        {
          description: 'Full names of all of the authors of the publication',
          id: 'publicationsAuthors',
          name: 'Authors',
          prop: 'authors',
          selected: true,
          width: 600,
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          dataType: 'char',
          xType: 'adql:VARCHAR',
          arraySize: '264*',
          operatorsTooltip: regexOperatorsTooltip
        }
      ],
      headerSearchFiltersMappingSearchProperties: {
        // Header search filters mapping search properties
        //    Position
        sourceNameResolver: {path: 'observations', property: 'observations.footprint', isNested: true},
        sourceName: {path: 'observations', property: 'observations.sourceName', isNested: true},
        raDec: {
          path: 'observations',
          property: {ra: 'observations.ra', dec: 'observations.dec', footprint: 'observations.footprint'},
          isNested: true
        },
        galactic: {
          path: 'observations',
          property: {
            longitude: 'observations.galacticLongitude',
            latitude: 'observations.galacticLatitude',
            ra: 'observations.ra',
            dec: 'observations.dec',
            footprint: 'observations.footprint'
          },
          isNested: true
        },
        ecliptic: {
          path: 'observations',
          property: {
            longitude: 'observations.eclipticLongitude',
            latitude: 'observations.eclipticLatitude',
            ra: 'observations.ra',
            dec: 'observations.dec',
            footprint: 'observations.footprint'
          },
          isNested: true
        },
        targetList: {
          path: 'observations',
          property: {
            sourceName: 'observations.sourceName',
            ra: 'observations.ra',
            dec: 'observations.dec',
            footprint: 'observations.footprint'
          },
          isNested: true
        },
        spatialResolution: {path: 'observations', property: 'observations.spatialResolution', isNested: true},
        largestAngularScale: {path: 'observations', property: 'observations.largestAngularScale', isNested: true},

        //    Energy
        frequency: {
          path: 'observations.energy',
          property: {frequencyMin: 'observations.energy.freqMin', frequencyMax: 'observations.energy.freqMax'},
          isNested: true
        },
        band: {path: 'observations', property: 'observations.band', isNested: true},
        spectralResolution: {path: 'observations.energy', property: 'observations.energy.resMin', isNested: true},
        continuumSensitivity: {path: 'observations', property: 'observations.continuumSensitivity', isNested: true},
        lineSensitivity: {path: 'observations', property: 'observations.lineSensitivity', isNested: true},

        //    Project
        projectCode: {path: 'projects', property: 'projects.code', isNested: true},
        projectTitle: {path: 'projects', property: 'projects.title', isNested: true},
        projectAbstract: {path: 'projects', property: 'projects.proposalAbstract', isNested: true},
        piName: {path: 'projects', property: 'projects.piName', isNested: true},
        proposalAuthors: {path: 'projects', property: 'projects.coiName', isNested: true},
        scienceKeywordIds: {path: 'projects', property: 'projects.scienceKeywordIds', isNested: true},

        //    Publication
        bibCode: {property: 'bibCode'},
        title: {property: 'title'},
        bibAbstract: {property: 'bibAbstract'},
        firstAuthor: {property: 'firstAuthor'},
        authors: {property: 'authors'},

        //    Observations
        observationDate: {path: 'observations', property: 'observations.observationDate', isNested: true},
        polarisationType: {path: 'observations.energy', property: 'observations.energy.polNum', isNested: true},
        mous: {path: 'observations', property: 'observations.mous', isNested: true},
        publicData: {path: 'observations', property: 'observations.releaseDate', isNested: true},
        objectTypes: {
          path: 'observations',
          objectTypesSimbad: {longtype: 'observations.longtypesSimbad', isBestMatch: 'observations.bestLongtypeSimbad'},
          objectTypesNed: {longtype: 'observations.longtypesNed', isBestMatch: 'observations.bestLongtypeNed'},
          isNested: true,
        },
        calibratorObservations: {path: 'observations', property: 'observations.isScience', isNested: true}
      },
      columnsSearchFiltersMappingSearchProperties: {
        publicationsBibCode: {property: 'bibCode'},
        publicationsFirstAuthor: {property: 'firstAuthor'},
        publicationsJournal: {property: 'journal'},
        publicationsYear: {property: 'publicationYear'},
        publicationsTitle: {property: 'title'},
        publicationsMaxReleaseDate: {property: 'releaseDate'},
        publicationsNumberProjects: {property: 'numberProjects'},
        publicationsNumberObservations: {property: 'numberObservations'},
        publicationsAuthors: {property: 'authors'}
      },
      aladinPolygonSearchFiltersMappingSearchProperties: {
        astrofilt: {
          path: 'observations',
          property: 'observations.footprint',
          isNested: true
        }
      },
      elasticDSLPositionAggregationQuery: {
        position: {
          nested: {
            path: 'observations'
          },
          aggs: {
            decMin: {min: {field: 'observations.decMin'}},
            decMax: {max: {field: 'observations.decMax'}},
            raMin: {min: {field: 'observations.raMin'}},
            raMax: {max: {field: 'observations.raMax'}},
            ra180Min: {min: {field: 'observations.ra180Min'}},
            ra180Max: {max: {field: 'observations.ra180Max'}}
          }
        }
      },
      elasticDSLFrequencyRangeAggregationQuery: {
        frequencyRange: {
          nested: {path: 'observations.energy'},
          aggs: {
            freqMin: {min: {field: 'observations.energy.freqMin'}},
            freqMax: {max: {field: 'observations.energy.freqMax'}}
          }
        },
      },
      elasticDSLRedshiftAggregationQuery: {
        redshift: {
          nested: {path: 'observations'},
          aggs: {
            redshiftAvg: {
              percentiles: {field: 'observations.redshift', percents: [50]}
            }
          }
        }
      }
    }
  }
];

@Component({
  selector: 'asax-result-table',
  template: `
    <ul ngbNav
        #nav="ngbNav"
        #navHeader
        class="nav-tabs pt-1"
        [activeId]="activeTabId"
        (activeIdChange)="onActiveTabIdChanged($event)"
        [animation]="false"
        [destroyOnHide]="false">
      <ng-container *ngFor="let tab of tabs">
        <li [ngbNavItem]="tab.id" 
            id="{{'tab-' + tab.id}}"
            class="selection-tab px-1"
            [ngClass]="{'disabled-tab': activeTabId !== tab.id}"
        >
          <ng-container [ngSwitch]="tab.type">
            <ng-container *ngSwitchCase="'search-table'">
              <a ngbNavLink>
                <i *ngIf="tab.icon" class="fa-light fa-{{tab.icon}} me-1"></i>
                <span [ngStyle]="{'font-weight': activeTabId !== tab.id ? 'normal' : 'bold' }">{{tab.title | titlecase}}</span>
                <ng-container *ngIf="tab.totalRows >= 0"> ({{tab.totalRows}})</ng-container>
              </a>
              <ng-template ngbNavContent>
                <asax-result-table-search-table-container [id]="tab.id"
                                                          [height]="height - navHeader.offsetHeight"
                                                          [width]="width"
                                                          [index]="tab.table.index"
                                                          [columns]="tab.table.columns"
                                                          [activeTabId]="activeTabId"
                                                          [headerSearchFiltersMappingSearchProperties]="tab.table.headerSearchFiltersMappingSearchProperties"
                                                          [columnsSearchFiltersMappingSearchProperties]="tab.table.columnsSearchFiltersMappingSearchProperties"
                                                          [positionAggregation]="tab.table.elasticDSLPositionAggregationQuery"
                                                          [frequencyRangeAggregation]="tab.table.elasticDSLFrequencyRangeAggregationQuery"
                                                          [redshiftAggregation]="tab.table.elasticDSLRedshiftAggregationQuery"
                                                          (outTotalRows)="tab.totalRows=$event">
                </asax-result-table-search-table-container>
              </ng-template>
            </ng-container>
          </ng-container>
        </li>
      </ng-container>
      <ng-container *ngFor="let voTable of voTables">
        <li [ngbNavItem]="voTable.id"
            class="me-2">
          <a ngbNavLink>
            <i class="fa-solid fa-circle mx-1" [style.color]="voTable.color"></i>
            <b>{{voTable.name}}</b>
            <ng-container *ngIf="voTable.rows?.length >= 0"> ({{voTable.rows?.length}})</ng-container>
            <button class="btn btn-sm btn-light p-0 px-1 m-0 ms-2"
                    [style.font-size.rem]="0.64375"
                    (click)="closeTab($event,voTable.id)">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </a>
          <ng-template ngbNavContent>
            <asax-result-table-vo-table-container [height]="height - navBarHeight"
                                                  [width]="width"
                                                  [id]="voTable.id"
                                                  [rows]="voTable.rows"
                                                  [columns]="voTable.columns">
            </asax-result-table-vo-table-container>
          </ng-template>
        </li>
      </ng-container>
    </ul>
    <div [ngbNavOutlet]="nav"></div>
  `,
  styleUrls: ['./asax-result-table.component.scss']
})
export class AsaxResultTableComponent {
  readonly navBarHeight = 45;

  @ViewChildren(TableContainerComponent) tableContainers: QueryList<TableContainerComponent>;
  @ViewChildren(VoTableContainerComponent) voTableContainers: QueryList<VoTableContainerComponent>;

  @Input() height: number;
  @Input() width: number;

  tabs: Tab[] = tabs;

  activeTabId: string;
  voTables: any = [];

  //If the tab id is changed, please update it also in table container 
  // in the ngOnInit function for the next check: if (this.id == 'observations')
  tabIds = ['observations', 'projects', 'publications'];

  constructor(private readonly asaxBookmarkingService: AsaxBookmarkingService,
              private readonly asaxResultTableService: AsaxResultTableService,
              private readonly asaxVoService: AsaxVoService) {
    this.asaxResultTableService.setActiveTab(tabs.find(tab => tab.id === activeTabIdDefault));
    this.asaxResultTableService.setActiveTabId(activeTabIdDefault);

    merge(
      this.asaxBookmarkingService.getQueryParam(activeTabIdQueryParam).pipe(
        filter(activeTabId => activeTabId !== undefined),
        filter(activeTabId => activeTabId !== this.asaxResultTableService.getActiveTabIdValue()),
        tap(activeTabId => this.asaxResultTableService.setActiveTab(tabs.find(tab => tab.id === activeTabId))),
      ),
      this.asaxVoService.getActiveTab()
    ).pipe(
      tap(activeTabId => this.asaxResultTableService.setActiveTabId(activeTabId)),
      tap(activeTabId => this.activeTabId = activeTabId)
    ).subscribe();

    this.asaxVoService.voServiceGenerated().pipe(
      tap(voService => this.tabIds.push(voService?.id)),
      tap(voService => this.voTables.push(voService)),
    ).subscribe();
  }

  onActiveTabIdChanged(activeTabId: string): void {
    if (activeTabId !== this.asaxResultTableService.getActiveTabIdValue()) {
      if (activeTabId === 'observations' || activeTabId === 'projects' || activeTabId === 'publications') {
        this.asaxResultTableService.setActiveTab(tabs.find(tab => tab.id === activeTabId));
        this.asaxBookmarkingService.addParamsToUrl({[activeTabIdQueryParam]: activeTabId});
        this.tableContainers?.find(tableContainer => tableContainer.id === activeTabId)?.updateTableView();
      } else {
        this.voTableContainers?.find(tableContainer => tableContainer.id === activeTabId)?.updateTableView();
      }
    }

    this.asaxResultTableService.setActiveTabId(activeTabId);
    this.activeTabId = activeTabId;
  }

  closeTab(event: MouseEvent, idToRemove: string) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.asaxResultTableService.getActiveTabIdValue() === idToRemove) {
      const index = this.tabIds.findIndex((id) => id === idToRemove);
      const nextActiveTabId = this.tabIds[index - 1] || this.voTables[index + 1];

      this.activeTabId = nextActiveTabId;
      this.asaxVoService.setActiveTabId(nextActiveTabId);
    }

    this.tabIds = this.tabIds.filter((id) => id !== idToRemove);
    this.voTables = this.voTables.filter(({id}) => id !== idToRemove);
    this.asaxVoService.removeVoService(idToRemove);
  }
}
